/**
 * Departments: the top level every project lives in (Dev, Field, Office, Personal…). A
 * department is a workflow, not a folder — it carries the operating rules its projects inherit:
 * colour, rate, billable, whether time is clocked, whether GPS is recorded while clocked in, and
 * whether its clock sessions feed BattleBooks timesheets. Replaces Signo's domain/subdomain.
 *
 * A project with no departmentId (or an unknown one) belongs to General. General always exists:
 * virtually from DEPARTMENT_DEFAULTS until someone edits it, then as the doc `departments/general`
 * (a fixed id, so two devices creating it write the same doc). Pure — no I/O.
 */

export const GENERAL_DEPARTMENT_ID = 'general';

export const DEPARTMENT_DEFAULTS = Object.freeze({
  name: 'General',
  color: '#8C8175',
  icon: '🗂️',
  order: 1e12,          // General sorts last unless moved
  clock: true,          // time here is clocked (sessions, live notice, timesheets)
  track: true,          // record GPS while clocked into its projects (the global setting is the ceiling)
  feedBooks: true,      // its clock sessions become BattleBooks timesheet entries
  billable: null,       // default for its projects; null = not billable unless the project says so
  hourlyRate: 0,        // default rate for its projects
});

/** A department doc with every setting filled in. */
export function withDepartmentDefaults(d) {
  if (!d) return { id: GENERAL_DEPARTMENT_ID, ...DEPARTMENT_DEFAULTS };
  const out = { ...DEPARTMENT_DEFAULTS, ...d };
  for (const k of Object.keys(DEPARTMENT_DEFAULTS)) if (out[k] === undefined) out[k] = DEPARTMENT_DEFAULTS[k];
  return out;
}

/** Every department in display order, General included (virtual if never saved). */
export function listDepartments(departments = []) {
  const byId = new Map(departments.filter((d) => d && d.id).map((d) => [d.id, withDepartmentDefaults(d)]));
  if (!byId.has(GENERAL_DEPARTMENT_ID)) byId.set(GENERAL_DEPARTMENT_ID, withDepartmentDefaults({ id: GENERAL_DEPARTMENT_ID }));
  return [...byId.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.name).localeCompare(String(b.name)));
}

/** The department a project belongs to (General when unset or unknown). */
export function departmentOf(project, departments = []) {
  const id = project?.departmentId;
  const found = id ? departments.find((d) => d && d.id === id) : null;
  if (found) return withDepartmentDefaults(found);
  return withDepartmentDefaults(departments.find((d) => d && d.id === GENERAL_DEPARTMENT_ID) || { id: GENERAL_DEPARTMENT_ID });
}

/** projectId → departmentId for every project (General for the unassigned). */
export function departmentIndex(projects = [], departments = []) {
  const m = new Map();
  for (const p of projects) if (p?.id) m.set(p.id, departmentOf(p, departments).id);
  return m;
}

export function resolveColor(project, dept) {
  return project?.color || dept?.color || '#3B82F6';
}

/** The project's own rate wins; else its department's. */
export function resolveRate(project, dept) {
  if (project?.hourlyRate) return project.hourlyRate;
  if (dept?.hourlyRate) return dept.hourlyRate;
  return 0;
}

/** A project's explicit true/false wins; null/undefined inherits the department's default. */
export function resolveBillable(project, dept) {
  if (project?.billable === true || project?.billable === false) return project.billable;
  return dept?.billable === true;
}

/** Clocked time? Personal-style departments set clock: false. */
export function departmentClocks(dept) {
  return dept?.clock !== false;
}

/**
 * Projects grouped under their departments, in department order. Folders with
 * parentType 'department' hang under their department with the projects filed in them.
 * @returns [{ ...department, projects: [...], folders: [{ ...folder, projects, children }] }]
 */
export function buildDepartmentTree(departments = [], projects = [], folders = []) {
  const list = listDepartments(departments);
  const byDept = new Map(list.map((d) => [d.id, { ...d, _nodeType: 'department', projects: [], folders: [] }]));
  const folderById = new Map(folders.map((f) => [f.id, { ...f, _nodeType: 'folder', projects: [], children: [] }]));

  for (const f of folderById.values()) {
    if (f.parentType === 'folder' && folderById.has(f.parentId)) folderById.get(f.parentId).children.push(f);
    else if (f.parentType === 'department' && byDept.has(f.parentId)) byDept.get(f.parentId).folders.push(f);
  }
  const bySort = (a, b) => (a.sortOrder ?? a.createdAt ?? 0) - (b.sortOrder ?? b.createdAt ?? 0);
  for (const p of projects) {
    const d = byDept.get(departmentOf(p, list).id);
    const folder = p.folderId ? folderById.get(p.folderId) : null;
    if (folder) folder.projects.push(p); else d.projects.push(p);
  }
  for (const d of byDept.values()) {
    d.projects.sort(bySort);
    d.folders.sort((a, b) => (a.order || 0) - (b.order || 0));
  }
  return [...byDept.values()];
}
